import { useEffect, useRef } from 'react';
import { X, Check, Undo2 } from 'lucide-react';
import { useConfetti } from './ConfettiCelebration';
import { analytics } from '../lib/analytics';

/**
 * ChoreCompletionModal - Centered modal for selecting who completed a chore
 *
 * Used by:
 * - SidebarChoreList.tsx (sidebar chore completion)
 * - Chores.tsx (main chores page completion)
 *
 * Features:
 * - Shows assigned members sorted alphabetically
 * - Incomplete members shown first (for completion)
 * - Completed members shown below (for undo)
 * - Triggers confetti on successful completion
 * - Escape key and click-outside to close
 */

interface Member {
  id: string;
  name: string;
  color: string;
  completed: boolean;
}

interface ChoreCompletionModalProps {
  /** The chore being completed */
  choreId: string;
  /** Title shown in modal header */
  choreTitle: string;
  /** Points awarded for completion */
  chorePoints: number;
  /** Chore recurrence type */
  choreRecurrence: 'daily' | 'weekly' | 'monthly' | 'once';
  /** For daily chores, who completed first */
  completedByMember?: {
    memberId: string;
    memberName: string;
    memberColor: string;
  };
  /** All assigned members with completion status */
  members: Member[];
  /** Called when a member is selected for completion/undo */
  onSelectMember: (memberId: string, isCompleted: boolean) => void;
  /** Called when modal should close */
  onClose: () => void;
  /** Source context for analytics */
  source: 'sidebar' | 'chores_page';
  /** Whether the completion mutation is pending */
  isPending?: boolean;
}

export function ChoreCompletionModal({
  choreId,
  choreTitle,
  chorePoints,
  choreRecurrence,
  completedByMember,
  members,
  onSelectMember,
  onClose,
  source,
  isPending = false,
}: ChoreCompletionModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { triggerConfetti } = useConfetti();

  // Track modal open
  useEffect(() => {
    analytics.choreCompletionModalOpened(choreId, source, members.length);
  }, [choreId, source, members.length]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        analytics.choreCompletionModalClosed(choreId, false);
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, choreId]);

  // Handle click outside
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      analytics.choreCompletionModalClosed(choreId, false);
      onClose();
    }
  };

  // Handle member selection
  const handleMemberClick = (member: Member) => {
    if (isPending) return;

    const isUndo = member.completed;

    // Call the mutation handler
    onSelectMember(member.id, member.completed);

    // Trigger confetti only for completions (not undos)
    if (!isUndo) {
      triggerConfetti();
      analytics.choreCompletionCelebrated(choreId, member.id, chorePoints);
    }

    analytics.choreCompletionModalClosed(choreId, true);
    onClose();
  };

  // Sort members: incomplete first, then alphabetically
  const sortedMembers = [...members].sort((a, b) => {
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });

  const incompleteMembers = sortedMembers.filter((m) => !m.completed);
  const completedMembers = sortedMembers.filter((m) => m.completed);
  const allCompleted = incompleteMembers.length === 0;

  // For daily chores, check if blocked
  const isDailyBlocked = choreRecurrence === 'daily' && completedByMember;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className="bg-bg-surface rounded-xl shadow-lg w-full max-w-sm animate-modal-enter"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Select who completed ${choreTitle}`}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-text-primary truncate">
              {choreTitle}
            </h2>
            <p className="text-xs text-text-secondary">
              {isDailyBlocked
                ? `Completed by ${completedByMember.memberName}`
                : allCompleted
                  ? 'Undo completion'
                  : 'Who completed this?'}
            </p>
          </div>
          <button
            onClick={() => {
              analytics.choreCompletionModalClosed(choreId, false);
              onClose();
            }}
            className="btn-icon flex-shrink-0 ml-2"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body - Member List */}
        <div className="p-2">
          {/* Daily chore completion message */}
          {isDailyBlocked && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-md flex-shrink-0"
                style={{ backgroundColor: completedByMember.memberColor }}
              />
              <p className="text-sm text-blue-800">
                <strong>{completedByMember.memberName}</strong> completed this
                daily chore. Only they can undo it today.
              </p>
            </div>
          )}

          {/* Incomplete members (for completion) */}
          {incompleteMembers.map((member) => {
            const isBlocked =
              isDailyBlocked && member.id !== completedByMember?.memberId;

            return (
              <button
                key={member.id}
                onClick={() => !isBlocked && handleMemberClick(member)}
                disabled={isPending || isBlocked}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  isBlocked
                    ? 'opacity-40 cursor-not-allowed'
                    : 'hover:bg-gray-50 disabled:opacity-50'
                }`}
              >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium text-white flex-shrink-0"
                style={{ backgroundColor: member.color }}
              >
                {member.name.charAt(0).toUpperCase()}
              </div>
                <span className="flex-1 text-left text-text-primary font-medium">
                  {member.name}
                </span>
                <span className="text-xs text-text-muted">+{chorePoints} pts</span>
              </button>
            );
          })}

          {/* Divider if both sections exist */}
          {incompleteMembers.length > 0 && completedMembers.length > 0 && (
            <div className="border-t border-border-light my-2" />
          )}

          {/* Completed members (for undo) - only show completer for daily chores */}
          {completedMembers
            .filter(
              (m) => !isDailyBlocked || m.id === completedByMember?.memberId
            )
            .map((member) => (
              <button
                key={member.id}
                onClick={() => handleMemberClick(member)}
                disabled={isPending}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors opacity-60 disabled:opacity-30"
              >
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-medium relative flex-shrink-0"
                style={{ backgroundColor: member.color }}
              >
                <span className="text-white">
                  {member.name.charAt(0).toUpperCase()}
                </span>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-2.5 h-2.5 text-white" />
                </div>
              </div>
              <span className="flex-1 text-left text-text-muted line-through">
                {member.name}
              </span>
              <Undo2 className="w-4 h-4 text-text-muted" />
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border-light bg-gray-50/50 rounded-b-xl">
          <p className="text-xs text-text-muted text-center">
            Tap a name to mark as complete
          </p>
        </div>
      </div>
    </div>
  );
}
